import { animateScroll } from 'react-scroll';
import { stopAudio } from './common/audio/audioActions';
import { stopSpeaking } from './common/avatar/avatarActions';
import {
    activateNextButton, unblockScreen, colorInScreen, enableDownButton,
} from './common/screen/screenActions';
import { startQuestionTimeTracking } from './common/timeTracking/timeTrackingActions';
import { stopPlaying } from './common/stimulus/stimulusButtonActions';
import selectors from './rootSelectors';
import {
    FLUENCY_MIN_SEGMENT_READING_SECS, FLUENCY_DOWN_BUTTON_AUTO_ADVANCE,
    FLUENCY_QUESTION_AUTO_ADVANCE, DAZE_DISCONTINUE_RULE,
} from './constants/time';


/* This is a set of helper functions to be used in Selenium tests.
  * Because of security concerns functions have hash names and can
  * be accessed from browser's console like: d394cd2151.b04e0d5b78()
  */

const ORIGINAL_FLUENCY_READING_TIMES = { ...FLUENCY_MIN_SEGMENT_READING_SECS };
const ORIGINAL_FLUENCY_SEGMENT_AUTO_ADVANCE = { ...FLUENCY_DOWN_BUTTON_AUTO_ADVANCE };
const ORIGINAL_FLUENCY_QUESTION_AUTO_ADVANCE = FLUENCY_QUESTION_AUTO_ADVANCE.FOR_ALL_GRADES;
const ORIGINAL_DAZE_DISCONTINUE_TIME = DAZE_DISCONTINUE_RULE.TIME;

const testHooks = ({ dispatch, getState }) => ({
    /* start question time tracking */
    b02e0e5b26: () => {
        dispatch(startQuestionTimeTracking());
    },

    /* Unblock screen with avatar and allow to proceed with further navigation.
         * Applicable for all screens with avatar and audio.
         * 1. Stop playing avatar audio instructions
         * 2. Unblock screen to allow clicks
         */
    b04e0d5b78: () => {
        dispatch(stopAudio());
        dispatch(stopSpeaking());
        dispatch(stopPlaying());
        dispatch(unblockScreen());
    },

    /* Unblock screen with avatar and allow to proceed with further navigation.
         * Applicable for Directions and Ready screens.
         * 1. Stop playing avatar audio instructions
         * 2. Unblock screen to allow clicks
         * 3. Show Ok button
         */
    a9d4c8be69: () => {
        dispatch(stopAudio());
        dispatch(stopSpeaking());
        dispatch(stopPlaying());
        dispatch(unblockScreen());
        dispatch(activateNextButton());
    },

    /* Unblock fluency type screen with avatar and allow to proceed
         * with further navigation.
         * Applicable for Model, Practice, Question screens.
         * 1. Stop playing avatar audio instructions
         * 2. Enable Down button
         * 3. Unblock screen to allow clicks
         */
    e6b34a27c5: () => {
        dispatch(stopAudio());
        dispatch(stopSpeaking());
        dispatch(stopPlaying());
        dispatch(unblockScreen());
        dispatch(colorInScreen());
        dispatch(enableDownButton());
    },


    /* Unblock comprehension type screen with avatar and allow to proceed
         * with further navigation.
         * Applicable for instruction screens in ready state.
         * 1. Stop playing avatar audio instructions
         * 2. Color in screen
         * 3. Unblock screen to allow clicks
         */
    c5b23b64e4: () => {
        dispatch(stopAudio());
        dispatch(stopSpeaking());
        dispatch(stopPlaying());
        dispatch(unblockScreen());
        dispatch(colorInScreen());
    },

    /* Scroll to bottom of TextPaneScroll
         * Applicable for
         * - Skip to DAZE passage last question
         */
    ac8d93b39f: () => {
        // Scroll passage content to bottom of pane
        animateScroll.scrollToBottom({
            containerId: 'containerElement',
        });
        // Set scroll dragger-bar to bottom of scroll pane
        const scrollBar = document.getElementsByClassName('dragger-bar')[0];
        scrollBar.style.top = null;
        scrollBar.style.bottom = '0px';
    },

    /* Returns logged student id
         */
    b811ac1226: () => {
        const state = getState();
        return selectors.getUserId(state);
    },


    /**
         * Set Fluency minimal segment reading time.
         * If not provided, time is set to 0 meaning popup will be disabled.
         */
    da92c0b6b3: (time = 0) => {
        Object.keys(FLUENCY_MIN_SEGMENT_READING_SECS).forEach((key) => {
            FLUENCY_MIN_SEGMENT_READING_SECS[key] = time;
        });
    },

    /* Re-enable Fluency reading reminder popup by restoring the minimum reading
         * time durations to original values.
        */
    if4956jre4: () => {
        Object.keys(FLUENCY_MIN_SEGMENT_READING_SECS).forEach((key) => {
            FLUENCY_MIN_SEGMENT_READING_SECS[key] = ORIGINAL_FLUENCY_READING_TIMES[key];
        });
    },

    /* Allows for setting the Fluency segment reading auto advance
         * time durations for all grade levels.
        */
    lguy6cnh94: (period) => {
        Object.keys(FLUENCY_DOWN_BUTTON_AUTO_ADVANCE).forEach((key) => {
            FLUENCY_DOWN_BUTTON_AUTO_ADVANCE[key] = period;
        });
    },

    /* Allows for setting the Fluency interstitial question auto advance
         * time durations for all grade levels.
        */
    b3088e6276: (period) => {
        FLUENCY_QUESTION_AUTO_ADVANCE.FOR_ALL_GRADES = period;
    },

    /* Restore Fluency auto advance time durations to original values for all
         * grade levels.
        */
    cah3i3n48b: () => {
        Object.keys(FLUENCY_DOWN_BUTTON_AUTO_ADVANCE).forEach((key) => {
            FLUENCY_DOWN_BUTTON_AUTO_ADVANCE[key] = ORIGINAL_FLUENCY_SEGMENT_AUTO_ADVANCE[key];
        });
        FLUENCY_QUESTION_AUTO_ADVANCE.FOR_ALL_GRADES = ORIGINAL_FLUENCY_QUESTION_AUTO_ADVANCE;
    },

    /* Set time for Daze discontinue rule.
         * Should be called before navigating to Daze Passage page.
        */
    b7f09c1db7: (period) => {
        DAZE_DISCONTINUE_RULE.TIME = period;
    },

    /* Restore original time for Daze discontinue rule
        */
    cda44f22a6: () => {
        DAZE_DISCONTINUE_RULE.TIME = ORIGINAL_DAZE_DISCONTINUE_TIME;
    },

    mockRecording: null,

    set d461719ecfc8 (mock) {
        this.mockRecording = mock;
    },

    get ff350510a0515 () {
        return this.mockRecording && new Blob([this.mockRecording], { type: 'audio/wav' });
    },

    saveAudioRecording: true,

    set a8c679594 (shouldSave) {
        this.saveAudioRecording = shouldSave;
    },

    get ab934dff () {
        return this.saveAudioRecording;
    }
});

export default testHooks;
